<template>

  <!-- Configuration Form -->
    <div class="min-w-0 overflow-hidden space-y-6">
    <!-- Schema Mapping -->
    <div class="min-w-0">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Reflection Guideline Schema
      </label>
      <div class="w-full min-w-0">
        <Select
          v-model="config.mapping"
          :options="availableSchemas"
          optionLabel="name"
          optionValue="_id"
          placeholder="Select a reflection guideline schema"
          class="w-full"
          :pt="{ root: { class: 'w-full' } }"
          @change="onConfigChange"
        />
      </div>
      <p class="text-xs text-gray-500 mt-1 break-words">
        Choose which schema will be used for reflection questions.
      </p>
    </div>

    <!-- Schedule Configuration (local time → stored as UTC) -->
    <div class="min-w-0">
      <h4 class="text-md font-medium text-gray-700 mb-3">Schedule Settings</h4>
      <p class="text-xs text-gray-500 mb-3">
        Set when to run in <strong>your</strong> local time.
        Stored as UTC (same as DevTips and Question Rotation).
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">

        <!-- Period -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Frequency
          </label>
          <div class="w-full min-w-0">
            <Select
              v-model="config.schedule.period"
              :options="periodOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @change="onConfigChange"
            />
          </div>
        </div>

        <!-- Day of Week (only for weekly) -->
        <div v-if="config.schedule.period === 'weekly'" class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Day of Week (your local time)
          </label>
          <div class="w-full min-w-0">
            <Select
              v-model="config.schedule.dayOfWeek"
              :options="dayOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @change="onConfigChange"
            />
          </div>
        </div>

        <!-- Day of Month (only for monthly) -->
        <div v-if="config.schedule.period === 'monthly'" class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Day of Month (your local time)
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.schedule.dayOfMonth"
              :min="1"
              :max="31"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>

        <!-- Hour (your local time) -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Hour (your local time, 0–23)
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.schedule.hour"
              :min="0"
              :max="23"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>

        <!-- Minute (local; cron runs hourly, minute for reference) -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Minute
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.schedule.minute"
              :min="0"
              :max="59"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>
      </div>
      <div
        class="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700"
      >
        <span class="font-medium">Stored as:</span>
        {{ reflexionStoredUtcLabel }}
        <span class="text-gray-500 block mt-1">
          (Runs at the time you set above, in your timezone.)
        </span>
      </div>
    </div>

    <!-- Questions Configuration -->
    <div class="min-w-0">
      <h4 class="text-md font-medium text-gray-700 mb-3">Question Settings</h4>
      <div class="flex flex-col gap-4 min-w-0">

        <!-- Slider Questions -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Slider Questions
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.questions.sliderQuestions"
              :min="0"
              :max="10"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>

        <!-- Text Questions -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Text Questions
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.questions.textQuestions"
              :min="0"
              :max="10"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>

        <!-- Total Questions -->
        <div class="min-w-0 w-full">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Total Questions
          </label>
          <div class="w-full min-w-0">
            <InputNumber
              v-model="config.questions.totalQuestions"
              :min="1"
              :max="20"
              class="w-full"
              :pt="{ root: { class: 'w-full' } }"
              @input="onConfigChange"
            />
          </div>
        </div>
      </div>
      <p class="text-xs text-gray-500 mt-2 break-words">
        Total questions should equal slider + text questions.
      </p>
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 min-w-0">
      <Button
        label="Reset"
        icon="pi pi-refresh"
        class="p-button-outlined p-button-sm w-full sm:w-auto"
        @click="resetConfig"
      />
      <Button
        label="Save Configuration"
        icon="pi pi-save"
        class="p-button-sm w-full sm:w-auto"
        :loading="isSaving"
        @click="saveConfig"
      />
    </div>
    </div>
</template>

<script>
import {
  Select,
  InputNumber,
  Button,
} from 'primevue';
import { mapActions, mapGetters } from 'vuex';
import {
  localScheduleToUtc,
  utcScheduleToLocal,
  formatStoredUtcSchedule,
} from '@/helpers/scheduleTimezoneHelper';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'ReflexionGuidelineConfigForm',
  components: {
    Select,
    InputNumber,
    Button,
  },
  props: {
    schemaId: {
      type: String,
      required: true,
    },
    initialConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isSaving: false,
      config: {
        mapping: null,
        schedule: {
          period: 'weekly',
          dayOfWeek: 4, // Thursday
          dayOfMonth: 1,
          hour: 8,
          minute: 0,
        },
        questions: {
          sliderQuestions: 1,
          textQuestions: 3,
          totalQuestions: 4,
        },
      },
      periodOptions: [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
      ],
      dayOptions: [
        { label: 'Sunday', value: 0 },
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
      ],
    };
  },
  mounted() {
    this.initializeConfig();
  },
  watch: {
    initialConfig: {
      handler() {
        this.initializeConfig();
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('assessmentSchemas', ['assessmentSchemas']),
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'reflexionGuideline',
        this.$t('FallbackTexts.reflectionGuideline'),
        this.$i18n?.locale,
      );
    },

    reflexionStoredUtcLabel() {
      const period = this.config.schedule?.period || 'weekly';
      const utc = localScheduleToUtc(
        period,
        this.config.schedule?.hour ?? 8,
        this.config.schedule?.dayOfWeek ?? 4,
        this.config.schedule?.dayOfMonth ?? 1,
      );
      return formatStoredUtcSchedule(
        period,
        utc.utcHour,
        utc.utcDayOfWeek,
        utc.utcDayOfMonth,
        this.dayOptions,
      );
    },

    // Filter available schemas (exclude current schema
    // and schemas that already have reflection guidelines enabled)
    availableSchemas() {
      if (!this.assessmentSchemas || !Array.isArray(this.assessmentSchemas)) {
        return [];
      }

      return this.assessmentSchemas.filter((schema) => {
        // Exclude current schema
        if (schema._id === this.schemaId) return false;

        // Exclude schemas that already have reflection guidelines enabled
        if (schema.config?.reflexionGuideline?.enabled) return false;

        return true;
      }).map((schema) => ({
        _id: schema._id,
        type: schema.type,
        name: schema.properties?.name || schema.type,
      }));
    },
  },
  methods: {
    ...mapActions('assessmentSchemas', ['updateReflexionGuidelineConfig']),

    initializeConfig() {
      if (this.initialConfig && Object.keys(this.initialConfig).length > 0) {
        const period = this.initialConfig.schedule?.period || 'weekly';
        const utcHour = this.initialConfig.schedule?.hour ?? 8;
        const utcDayOfWeek = this.initialConfig.schedule?.dayOfWeek ?? 4;
        const utcDayOfMonth = this.initialConfig.schedule?.dayOfMonth ?? 1;
        const local = utcScheduleToLocal(
          period,
          utcHour,
          utcDayOfWeek,
          utcDayOfMonth,
        );
        this.config = {
          mapping: this.initialConfig.mapping || null,
          schedule: {
            period,
            dayOfWeek: local.localDayOfWeek ?? 4,
            dayOfMonth: local.localDayOfMonth ?? 1,
            hour: local.localHour,
            minute: this.initialConfig.schedule?.minute ?? 0,
          },
          questions: {
            sliderQuestions: this.initialConfig.questions?.sliderQuestions || 1,
            textQuestions: this.initialConfig.questions?.textQuestions || 3,
            totalQuestions: this.initialConfig.questions?.totalQuestions || 4,
          },
        };
      }
    },

    onConfigChange() {
      // Auto-calculate total questions
      if (this.config.questions.sliderQuestions + this.config.questions.textQuestions
       !== this.config.questions.totalQuestions) {
        this.config.questions.totalQuestions = this.config.questions.sliderQuestions
         + this.config.questions.textQuestions;
      }
    },

    resetConfig() {
      this.initializeConfig();
    },

    async saveConfig() {
      if (!this.initialConfig?.enabled) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: `Please enable ${this.featureName} first`,
          life: 3000,
        });
        return;
      }

      if (this.initialConfig?.enabled && !this.config.mapping) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: `Please select a ${this.featureName} schema`,
          life: 3000,
        });
        return;
      }

      this.isSaving = true;
      try {
        const period = this.config.schedule?.period || 'weekly';
        const utc = localScheduleToUtc(
          period,
          this.config.schedule?.hour ?? 8,
          this.config.schedule?.dayOfWeek ?? 4,
          this.config.schedule?.dayOfMonth ?? 1,
        );
        const scheduleToSave = {
          period,
          hour: utc.utcHour,
          dayOfWeek: utc.utcDayOfWeek ?? 4,
          dayOfMonth: utc.utcDayOfMonth ?? 1,
          minute: this.config.schedule?.minute ?? 0,
        };
        await this.updateReflexionGuidelineConfig({
          id: this.schemaId,
          config: {
            ...this.config,
            schedule: scheduleToSave,
            enabled: this.initialConfig?.enabled || false,
          },
        });

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: `${this.featureName} configuration saved successfully`,
          life: 3000,
        });

        this.$emit('config-saved', this.config);
      } catch (error) {
        console.error('Error saving config:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to save ${this.featureName} configuration`,
          life: 3000,
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.reflexion-guideline-config {
  @apply bg-white rounded-lg border border-gray-200 p-6;
}
</style>
